import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter as FilterIcon, Plus, Receipt } from 'lucide-react';
import {
  ButtonCreate,
  FilterWrapper,
  SectionName,
  Tab,
  TabsDiv,
  OptionsButton,
} from '../../../components/styles/subheader-style';
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu';
import { Filter } from '../../../components/SubHeaderFilter/types';
import { FinancialEntryListFilters } from './types';
import {
  FINANCIAL_VIEW_BY,
  FINANCIAL_VIEW_BY_OPTIONS,
  FinancialEntrySortByValue,
  FinancialViewByValue,
  getDefaultSortForView,
  getSortByLabel,
  getSortOptionsForView,
  getViewByLabel,
} from './constants';
import { getFinancialFilterHeaders } from './financialFilterConfig';
import FinancialOptionDropdown from './FinancialEntriesTable/FinancialOptionDropdown';
import { financialEntryRoutes } from './routes';
import {
  FilterTriggerButton,
  FinancialHeaderWrapper,
  FinancialLeftSide,
  FinancialRightSide,
  HeaderFilterGroup,
  HeaderFilterLabel,
  HeaderFilterSeparator,
  HeaderTopRow,
} from './style';
import { DateInput } from './FinancialEntriesTable/style';

interface FinancialListHeaderProps {
  filters: FinancialEntryListFilters;
  onFiltersChange: (patch: Partial<FinancialEntryListFilters>) => void;
}

const FinancialListHeader: React.FC<FinancialListHeaderProps> = ({
  filters,
  onFiltersChange,
}) => {
  const navigate = useNavigate();
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const appliedFilters = filters.appliedFilters ?? [];
  const viewBy = filters.viewBy ?? FINANCIAL_VIEW_BY.ENTRY;
  const sortOptions = getSortOptionsForView(viewBy);
  const filterHeaders = getFinancialFilterHeaders(viewBy);

  const handleApplyFilter = (filter: Filter) => {
    onFiltersChange({
      appliedFilters: [...appliedFilters, filter],
    });
  };

  return (
    <FinancialHeaderWrapper>
      <HeaderTopRow>
        <FinancialLeftSide>
          <SectionName>Financeiro</SectionName>
          <TabsDiv>
            <Tab $isFirst $isLast $isActive>
              <Receipt size={12} style={{ marginRight: 6 }} />
              Lançamentos
            </Tab>
          </TabsDiv>

          <HeaderFilterSeparator />

          <HeaderFilterGroup>
            <HeaderFilterLabel>Período:</HeaderFilterLabel>
            <HeaderFilterLabel style={{ fontWeight: 400, color: '#696969' }}>De</HeaderFilterLabel>
            <DateInput
              type="date"
              className="inline-date"
              value={filters.createdAtFrom?.slice(0, 10) ?? ''}
              onChange={(e) =>
                onFiltersChange({
                  createdAtFrom: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined,
                })
              }
            />
            <HeaderFilterLabel style={{ fontWeight: 400, color: '#696969' }}>Até</HeaderFilterLabel>
            <DateInput
              type="date"
              className="inline-date"
              value={filters.createdAtTo?.slice(0, 10) ?? ''}
              onChange={(e) =>
                onFiltersChange({
                  createdAtTo: e.target.value ? `${e.target.value}T23:59:59.999Z` : undefined,
                })
              }
            />
          </HeaderFilterGroup>

          <HeaderFilterSeparator />

          <HeaderFilterGroup>
            <HeaderFilterLabel>Ordenar por</HeaderFilterLabel>
            <FinancialOptionDropdown
              value={filters.sortBy ?? getDefaultSortForView(viewBy)}
              options={sortOptions}
              getLabel={(sortBy) => getSortByLabel(sortBy, viewBy)}
              onChange={(sortBy: FinancialEntrySortByValue) => onFiltersChange({ sortBy })}
            />
          </HeaderFilterGroup>

          <HeaderFilterGroup>
            <HeaderFilterLabel>Visualizar por</HeaderFilterLabel>
            <FinancialOptionDropdown
              value={viewBy}
              options={FINANCIAL_VIEW_BY_OPTIONS}
              getLabel={getViewByLabel}
              onChange={(nextViewBy: FinancialViewByValue) => {
                const nextSortOptions = getSortOptionsForView(nextViewBy);
                const currentSort = filters.sortBy ?? getDefaultSortForView(viewBy);
                const sortBy = nextSortOptions.some((option) => option.value === currentSort)
                  ? currentSort
                  : getDefaultSortForView(nextViewBy);

                onFiltersChange({
                  viewBy: nextViewBy,
                  sortBy,
                  appliedFilters: [],
                });
              }}
            />
          </HeaderFilterGroup>
        </FinancialLeftSide>

        <FinancialRightSide>
          <FilterWrapper>
            <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
              <FilterIcon size={18} />
            </OptionsButton>
            {isFilterMenuOpen && (
              <FilterMenu
                headers={filterHeaders}
                onApplyFilter={(f) => {
                  handleApplyFilter(f);
                  setIsFilterMenuOpen(false);
                }}
                onClose={() => setIsFilterMenuOpen(false)}
                anchorRef={filterButtonRef}
                filters={appliedFilters}
                align="end"
              />
            )}
          </FilterWrapper>

          <ButtonCreate onClick={() => navigate(financialEntryRoutes.new)}>
            <Plus size={14} style={{ marginRight: 6 }} />
            Novo lançamento
          </ButtonCreate>
        </FinancialRightSide>
      </HeaderTopRow>
    </FinancialHeaderWrapper>
  );
};

export default FinancialListHeader;
